import React, { useState, useRef, useEffect } from 'react';
import {
  Preloader,
  FormButton,
  Button,
  Alert,
} from '@cochlear-design-system/foundation';
import {
  AddressMaintenanceListView,
  AddressMaintenanceSingleEntryView,
} from '../../../ui/Address';
import { convertPatientDataToAddressMaintenance } from '../dataMapper';
import { getReadConfig, getEditConfig } from '../addressConfig';
import useUpdateAddress from '../../../hooks/useUpdateAddress';
import { logger } from '../../../logger';

const log = logger();

export const AddressView = ({
  labels,
  patientDetails,
  loading,
  providerId,
  providerCountryCode,
  googleMapsApiKey,
  isCarer = false,
}) => {
  const [mode, setMode] = useState('read'); // read, create or update
  const [activeAddress, setActiveAddress] = useState();
  const [saveButtonProgress, setSaveButtonProgress] = useState(1);
  const [saveButtonText, setSaveButtonText] = useState(
    labels['labels.common.save'],
  );
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccessAlert, setShowSuccessAlert] = useState(false);
  const addressFormRef = useRef();
  const formValuesRef = useRef({
    countryIso2Code: '',
    countryName: '',
    value: {
      id: '',
      address: {
        addressText: '',
        street: [],
        city: '',
        state: '',
        stateCode: '',
        postalCode: '',
      },
      isPO: false,
    },
    isBilling: false,
    isShipping: false,
    isMailing: false,
  });

  // Initialize the useUpdateAddress hook
  const patientId = patientDetails?.id;
  const updateAddressMutation = useUpdateAddress(
    providerId,
    patientId,
  );

  // Warn user if they try to leave while mutation is pending
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (updateAddressMutation.isPending) {
        e.preventDefault();
        e.returnValue = ''; // Chrome requires returnValue to be set
      }
      return undefined;
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [updateAddressMutation.isPending]);

  const handleOnChange = (formValues) => {
    const values = formValues; // Update the ref with the new form values
    formValuesRef.current = values;
  };

  const readConfig = getReadConfig(
    patientDetails,
    labels,
    setActiveAddress,
    setMode,
    providerCountryCode,
    isCarer,
  );
  const editConfig = getEditConfig(
    handleOnChange,
    providerCountryCode,
    googleMapsApiKey,
  );

  const addressMaintenanceData =
    convertPatientDataToAddressMaintenance(patientDetails);

  const handleOnSave = async () => {
    // Access the form values from the ref
    const formValues = formValuesRef.current;

    if (!formValues) {
      return;
    }

    // Get the address ID from the form values
    // This ID comes from the patient details response (via dataMapper)
    const addressID = formValues.value?.id;

    // Transform the form values into the required structure
    const transformedData = {
      id: addressID, // Always include the address ID
      type: 'address', // Explicitly set to the string literal "address"
      attributes: {
        street: formValues.value?.address?.street?.join(', ') || '', // Extract the first street line
        city: formValues.value?.address?.city || '',
        stateName: formValues.value?.address?.state || '',
        stateCode: formValues.value?.address?.stateCode || '',
        postalCode: formValues.value?.address?.postalCode || '',
        countryCode: formValues.countryIso2Code || '',
        addressTypes: [
          ...(formValues.isBilling ? ['Billing'] : []),
          ...(formValues.isShipping ? ['Shipping'] : []),
          ...(formValues.isMailing ? ['Mailing'] : []),
        ], // Dynamically add address types based on flags
      },
    };

    // Optimistically switch back to read mode immediately
    setMode('read');
    setActiveAddress(null);

    // Show success alert
    setShowSuccessAlert(true);

    // Hide alert after 5 seconds
    setTimeout(() => {
      setShowSuccessAlert(false);
    }, 5000);

    // Call the mutation in the background (don't await)
    updateAddressMutation.mutate(transformedData, {
      onError: (error) => {
        // If it fails, switch back to edit mode and hide alert
        log.error('Failed to save address:', error);
        setMode('update');
        setActiveAddress(formValues);
        setShowSuccessAlert(false);
      },
    });
  };

  const handleSaveClick = async (e) => {
    e.preventDefault();
    if (addressFormRef.current) {
      try {
        const validationErrors =
          await addressFormRef.current.doValidateForm(true);

        if (Object.keys(validationErrors).length === 0) {
          setSaveButtonText(labels['labels.common.saving']);
          setIsSubmitting(true);
          setSaveButtonProgress(0);

          // Wait 1 second to show "Saving..." state
          await new Promise((resolve) => setTimeout(resolve, 1000));

          // Save (no await - happens in background)
          handleOnSave();

          // Show saved state
          setSaveButtonText(
            labels['labels.common.saved'] || 'Saved!',
          );
          setSaveButtonProgress(1);
          setIsSubmitting(false);

          setTimeout(() => {
            setSaveButtonText(labels['labels.common.save']);
          }, 2000);
        }
      } catch (error) {
        // Error during save - keep in edit mode
        log.error('Save error:', error);
      } finally {
        setSaveButtonProgress(1);
        setIsSubmitting(false);
      }
    }
  };

  return (
    <section className="ccl-c-form-section">
      <div className="ccl-e-detail-action-bar">
        <div className="ccl-e-detail-action-bar__title">
          <span>
            {mode === 'update'
              ? labels['labels.editPatient.editAddress.title']
              : labels['labels.editPatient.addressDetails.title']}
          </span>
        </div>
      </div>

      {showSuccessAlert && mode === 'read' && (
        <div className="mb-3">
          <Alert variant="success" dismissible>
            {labels['labels.editPatient.addressUpdated'] ||
              'Address has been updated successfully'}
          </Alert>
        </div>
      )}

      <div>
        {loading ? (
          <Preloader
            key={`preloader${Math.random()}`}
            lines={['title', 'label-field', 'label-field']}
          />
        ) : (
          <div>
            {mode === 'update' ? (
              <>
                <AddressMaintenanceSingleEntryView
                  ref={addressFormRef}
                  config={editConfig}
                  data={addressMaintenanceData}
                  labels={labels}
                  activeItem={activeAddress}
                  onChange={editConfig.onChange}
                  onSave={editConfig.onSave}
                />
                <div className="d-flex flex-row pt-4">
                  <div className="pe-2">
                    <FormButton
                      disabled={isSubmitting}
                      onClick={handleSaveClick}
                      text={saveButtonText}
                      progress={saveButtonProgress}
                      type="save"
                    />
                  </div>
                  <div>
                    <Button
                      disabled={isSubmitting}
                      text={labels['labels.common.cancel']}
                      variant="tertiary"
                      onClick={() => {
                        setMode('read');
                        setActiveAddress(null);
                      }}
                    />
                  </div>
                </div>
              </>
            ) : (
              <AddressMaintenanceListView
                config={readConfig}
                data={addressMaintenanceData}
                labels={labels}
              />
            )}
          </div>
        )}
      </div>
    </section>
  );
};
